import React, { useEffect, useRef, useState } from 'react';
import api from '../../api/axios';
import { errorMessage } from '../../api/catalogImportApi';
import { btnGhost, btnPrimary, btnSmall, pill } from './styles';
import './ComparisonNotebook.css';

const inr = (v) => (v === null || v === undefined || v === '' ? '' : `₹${Number(v).toLocaleString('en-IN')}`);
const dims = (l, w, h, u) => ([l, w, h].some((x) => x !== null && x !== undefined && x !== '')
  ? `${l ?? '–'} × ${w ?? '–'} × ${h ?? '–'} ${u || ''}`.trim() : '');
const cat = (c, s) => [c, s].filter(Boolean).join(' › ');

const VERDICT = {
  SAME: { text: 'Same product', style: pill('#FEE2E2', '#B91C1C') },
  VARIANT: { text: 'Variant — another size/colour', style: pill('#FEF3C7', '#92400E') },
  DIFFERENT: { text: 'Different product', style: pill('#F3F4F6', '#4B5563') },
};
const SOURCE = { RULE_NAME: 'name rule', RULE_PHOTO: 'photo match', AI: 'AI', NAME_MATCH: 'name match only' };

/** Loose equality for the =/≠ markers: case, spacing, punctuation and list order don't count. */
const norm = (v) => String(v ?? '').toLowerCase().replace(/[^a-z0-9₹.]+/g, ' ').trim();
const asSet = (v) => norm(v).split(' ').filter(Boolean).sort().join(' ');
const same = (a, b, kind) => {
  if (a === '' || b === '' || a == null || b == null) return null; // can't compare a missing value
  if (kind === 'list') return asSet(a) === asSet(b);
  return norm(a) === norm(b);
};

/** The fields, in the same order on both pages. */
const FIELDS = [
  { key: 'name', label: 'Name', draft: (d) => d.name, existing: (p) => p.name },
  { key: 'brand', label: 'Brand', draft: (d) => d.brandName, existing: (p) => p.brand },
  { key: 'mrp', label: 'MRP (incl. GST)', draft: (d) => inr(d.mrp), existing: (p) => inr(p.mrp) },
  { key: 'mop', label: 'MOP (excl. GST)', draft: (d) => inr(d.marketOperatingPrice), existing: (p) => inr(p.marketOperatingPrice) },
  { key: 'colors', label: 'Colours', draft: (d) => d.colors, existing: (p) => p.colors, kind: 'list' },
  { key: 'sizes', label: 'Sizes', draft: (d) => d.sizes, existing: (p) => p.sizes, kind: 'list' },
  { key: 'options', label: 'Options', draft: (d) => (d.variants || []).map((v) => [v.size, v.color].filter(Boolean).join(' / ')).join(', '),
    existing: (p) => (p.variants || []).map((v) => v.label).join(', '), kind: 'list' },
  { key: 'dimensions', label: 'Dimensions', draft: (d) => dims(d.dimensionL, d.dimensionW, d.dimensionH, d.dimensionUnit),
    existing: (p) => dims(p.dimensionL, p.dimensionW, p.dimensionH, p.dimensionUnit) },
  { key: 'category', label: 'Category', draft: (d) => cat(d.categoryName, d.subcategoryName), existing: (p) => cat(p.category, p.subcategory) },
  { key: 'description', label: 'Description', draft: (d) => d.description, existing: (p) => p.description, long: true },
];

/** Large photo plus thumbnails; remounted (key) per product so it starts on the main photo. */
function Photos({ urls, label }) {
  const [active, setActive] = useState(0);
  return (
    <>
      <div className="cmp-photo">
        {urls.length ? <img className="cmp-photo-main" src={urls[active]} alt={`${label} (${active + 1} of ${urls.length})`} />
          : <div className="cmp-photo-empty">No photo</div>}
      </div>
      <div className="cmp-thumbs">
        {urls.length > 1 && urls.map((u, i) => (
          <button key={u} type="button" className="cmp-thumb" aria-pressed={i === active} onClick={() => setActive(i)}
                  aria-label={`Show ${label} photo ${i + 1}`} style={{ backgroundImage: `url("${u}")`, backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />
        ))}
      </div>
    </>
  );
}

/**
 * "Similar item detected", opened like a notebook: the catalog draft on the left page, the existing
 * product on the right, one ruled line per field with = / ≠ so differences jump out. Flip between
 * look-alikes with ‹ › (or ← →). The decision buttons are the panel's own actions.
 */
export default function ComparisonNotebook({ item, candidates, startIndex = 0, campaign, busy,
  onKeepExisting, onUpdateExisting, onAddOption, onNotSame, onClose }) {
  const [index, setIndex] = useState(Math.min(startIndex, candidates.length - 1));
  const [products, setProducts] = useState({}); // productId -> full product (or { error })
  const closeRef = useRef(null);
  const c = candidates[index];
  const product = c ? products[c.productId] : null;

  useEffect(() => { closeRef.current?.focus(); }, []);

  useEffect(() => {
    if (!c || products[c.productId]) return;
    api.get(`/products/${c.productId}`)
      .then((r) => setProducts((prev) => ({ ...prev, [c.productId]: r.data })))
      .catch((err) => setProducts((prev) => ({ ...prev, [c.productId]: { error: errorMessage(err, 'Could not load this product.') } })));
  }, [c, products]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setIndex((i) => Math.min(candidates.length - 1, i + 1));
      if (e.key === 'ArrowLeft') setIndex((i) => Math.max(0, i - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [candidates.length, onClose]);

  if (!c) return null;
  const v = c.verdict ? VERDICT[c.verdict] : null;
  const draftPhotos = (item.images || []).map((img) => img.url);
  const existingPhotos = product && !product.error ? (product.imageUrls || []) : (c.imageUrl ? [c.imageUrl] : []);

  return (
    <div className="cmp-overlay" role="dialog" aria-modal="true" aria-label="Compare with existing product"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cmp-book">
        <div className="cmp-top">
          <h2>Similar item detected</h2>
          <span className="cmp-count">{candidates.length > 1 ? `${index + 1} of ${candidates.length} look-alikes` : '1 look-alike'}</span>
          <div className="cmp-nav">
            <button type="button" className="cmp-icon-btn" disabled={index === 0} onClick={() => setIndex(index - 1)} aria-label="Previous look-alike">‹</button>
            <button type="button" className="cmp-icon-btn" disabled={index === candidates.length - 1} onClick={() => setIndex(index + 1)} aria-label="Next look-alike">›</button>
            <button type="button" className="cmp-icon-btn" ref={closeRef} onClick={onClose} aria-label="Close comparison">✕</button>
          </div>
        </div>

        <div className="cmp-spread">
          <div className="cmp-page-head">From the catalog · page {item.pageNumber}</div>
          <div className="cmp-page-head">
            In your catalogue · <a href={`/product/${c.productId}`} target="_blank" rel="noreferrer">#{c.productId} ↗</a>
            {c.approvalStatus && c.approvalStatus !== 'APPROVED' && <> · {c.approvalStatus.toLowerCase()}</>}
          </div>

          <div><Photos urls={draftPhotos} label="catalog draft" /></div>
          <div><Photos key={c.productId} urls={existingPhotos} label="existing product" /></div>

          {FIELDS.map((f) => {
            const left = f.draft(item) ?? '';
            const right = product && !product.error ? (f.existing(product) ?? '') : '';
            const eq = product && !product.error ? same(left, right, f.kind) : null;
            if (left === '' && right === '' && f.key !== 'name') return null; // nothing on either page
            const cls = eq === true ? 'cmp-same' : eq === false ? 'cmp-diff' : '';
            return (
              <div className="cmp-row" key={f.key}>
                <div className={`cmp-cell ${eq === false ? 'cmp-diff' : ''}`}>
                  <span className="cmp-label">{f.label}</span>
                  <span className={f.long ? 'cmp-desc' : undefined} style={f.long ? { display: 'block' } : undefined}>
                    {left === '' ? <span className="cmp-empty">not in catalog</span> : left}
                  </span>
                </div>
                <div className={`cmp-cell ${cls}`}>
                  <span className="cmp-label">{f.label}</span>
                  {!product ? <span className="cmp-empty">loading…</span>
                    : product.error ? <span className="cmp-empty">{product.error}</span>
                    : (
                      <span className={f.long ? 'cmp-desc' : undefined} style={f.long ? { display: 'block' } : undefined}>
                        {eq !== null && <span className="cmp-mark" aria-label={eq ? 'same' : 'different'}>{eq ? '=' : '≠'}</span>}
                        {right === '' ? <span className="cmp-empty">empty</span> : right}
                      </span>
                    )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="cmp-verdict">
          <strong>Verdict:</strong>
          {v ? <span style={v.style}>{v.text}</span> : <span style={pill('#EDE9FE', '#5B21B6')}>Not judged — similar name</span>}
          {c.source && SOURCE[c.source] && <span style={{ color: '#6B7280' }}>· {SOURCE[c.source]}</span>}
          {c.source === 'AI' && c.confidence && <span style={{ color: '#6B7280' }}>· {c.confidence.toLowerCase()} confidence</span>}
          {c.reason && <span style={{ fontStyle: 'italic', color: '#4B5563' }}>— “{c.reason}”</span>}
          {c.dismissed && <span style={pill('#F3F4F6', '#6B7280')}>you marked this as not the same</span>}
        </div>

        <div className="cmp-actions">
          <button type="button" style={btnGhost} disabled={busy} onClick={onNotSame}
                  title="None of the look-alikes is this product — create it as a new product">
            {candidates.length > 1 ? 'None of these — create new' : 'Not the same — create new'}
          </button>
          <button type="button" style={c.verdict === 'VARIANT' ? btnPrimary : btnSmall} disabled={busy}
                  onClick={() => onAddOption(c.productId)}>
            Add as an option of #{c.productId}…
          </button>
          <button type="button" style={btnSmall} disabled={busy} onClick={() => onUpdateExisting(c.productId)}>
            Update #{c.productId} from catalog…
          </button>
          <button type="button" style={c.verdict === 'VARIANT' ? btnSmall : btnPrimary} disabled={busy}
                  onClick={() => onKeepExisting(c)}>
            Keep #{c.productId}{campaign ? ' & add to campaign' : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
